<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }} </BaseButton>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else-if="campaign">
            <header class="flex items-start justify-between gap-4 flex-wrap">
                <div class="flex flex-col gap-1 min-w-0">
                    <h2 class="title-subsection text-ink">{{ campaign.name }}</h2>
                    <I18nT keypath="reflections.campaignReport.summary" tag="p" class="body-small text-ink-muted">
                        <template #template>{{ campaign.template.name }}</template>
                        <template #participants>
                            <span class="body-small-bold text-ink">{{ t("reflections.campaignReport.participantsCount", { count: campaign.cycle_count }) }}</span>
                        </template>
                        <template #responses>{{ campaign.submitted_response_count }}</template>
                    </I18nT>
                    <p v-if="report?.generated_at" class="details text-ink-muted">
                        {{ t("reflectionsAdmin.campaign.reportGenerated", { date: formatDate(report.generated_at, "short") }) }}
                        <span v-if="newSubmissionsSinceReport > 0" class="text-ink body-small-bold">
                            {{ t("reflections.campaignReport.newSubmissionsSinceReport", { count: newSubmissionsSinceReport }) }}
                        </span>
                    </p>
                </div>
                <div class="flex items-center gap-2 flex-wrap shrink-0">
                    <BaseButton theme="neutral" size="sm" :disabled="downloading || !report" @click="onDownloadPdf">
                        <i class="bi bi-download" aria-hidden="true"></i>
                        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: loading ellipsis placeholder -->
                        {{ downloading ? "…" : t("reflections.campaignReport.downloadPdf") }}
                    </BaseButton>
                    <BaseTooltip :disabled="campaignClosed">
                        <template #trigger>
                            <!-- A disabled button swallows hover, so the span carries the tooltip. -->
                            <span class="inline-flex">
                                <BaseButton theme="neutral" size="sm" :disabled="downloadingCsv || !campaignClosed" @click="onDownloadCsv">
                                    <i class="bi bi-filetype-csv" aria-hidden="true"></i>
                                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: loading ellipsis placeholder -->
                                    {{ downloadingCsv ? "…" : t("reflections.campaignReport.downloadCsv") }}
                                </BaseButton>
                            </span>
                        </template>
                        <template #content>{{ t("reflectionsAdmin.results.csvAvailableWhenClosed") }}</template>
                    </BaseTooltip>
                    <p role="status" class="body-small text-status-error empty:sr-only">{{ csvError }}</p>
                    <BaseButton theme="primary" size="sm" :disabled="regenerating || belowFloor" @click="onRegenerate">
                        <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
                        {{ regenerating ? t("reflections.campaignReport.queued") : t("reflections.campaignReport.regenerate") }}
                    </BaseButton>
                </div>
            </header>

            <BaseNotification v-if="regenStatus" :theme="regenStatus.theme" :text="regenStatus.text" @dismiss="regenStatus = null" />

            <HorizontalDivider as="hr" spacing="none" />

            <BaseCard v-if="!report" padding="lg" class="text-center">
                <p class="body-small text-ink-muted">{{ reportEmptyMessage }}</p>
            </BaseCard>

            <PooledReportBody v-else :report="report" />

            <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
        </template>
    </section>
</template>

<script setup lang="ts">
import { fetchApi, useFetch } from "~vue/api";
import type { CampaignAdminView, CampaignReportPayload, CampaignResultsResponse } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import PooledReportBody from "~vue/pages/reflections/PooledReportBody.vue";
import { useReportRegeneration } from "~vue/pages/reflections/useReportRegeneration";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CAMPAIGN_NOT_CLOSED_CODE, exportErrorCode, MIN_EXPORT_PARTICIPANTS, TOO_FEW_PARTICIPANTS_CODE } from "~vue/utils/exportErrorCode";
import { logUserInteraction } from "~vue/utils/logUtils";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

interface Props {
    campaignId: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (event: "back"): void }>();
const { t } = useI18n();

const campaign = ref<CampaignAdminView | null>(null);
const report = ref<CampaignReportPayload | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const downloading = ref(false);
const downloadingCsv = ref(false);
const csvError = ref<string | null>(null);

function csvErrorMessage(code: string | null): string {
    switch (code) {
        case TOO_FEW_PARTICIPANTS_CODE:
            return t("reflectionsAdmin.results.csvTooFewParticipants", { count: MIN_EXPORT_PARTICIPANTS });
        case CAMPAIGN_NOT_CLOSED_CODE:
            return t("reflectionsAdmin.results.csvAvailableWhenClosed");
        default:
            return t("reflectionsAdmin.results.csvFailed");
    }
}
// Raw rows could be diffed against the Raters tab while responses still arrive.
const campaignClosed = computed(() => campaign.value?.state === "closed");

// Below the participant floor no report can exist; the worker would only skip the build.
const belowFloor = computed(() => {
    const current = campaign.value;
    return !!current && current.report_participant_count < current.min_report_participants;
});

const reportEmptyMessage = computed(() => {
    const current = campaign.value;
    if (current && belowFloor.value) {
        return t("reflections.campaignReport.waitingOnParticipants", { count: current.report_participant_count, needed: current.min_report_participants });
    }
    return t("reflections.campaignReport.noReport");
});

const campaignDemoActions = computed(() => {
    const currentCampaign = campaign.value;
    return currentCampaign && currentCampaign.state !== "closed" ? [{ id: currentCampaign.id, label: currentCampaign.name }] : [];
});

const newSubmissionsSinceReport = computed(() => {
    if (!campaign.value || !report.value) {
        return 0;
    }
    return Math.max(0, (campaign.value.submitted_response_count ?? 0) - (report.value.response_count_at_generation ?? 0));
});

async function fetchResults(): Promise<CampaignResultsResponse | null> {
    const { data } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/results`).json<CampaignResultsResponse>();
    return data.value ?? null;
}

async function onDownloadPdf() {
    if (downloading.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_PDF_DOWNLOAD_CLICKED, { campaign_id: props.campaignId });
    downloading.value = true;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaignId}/export-pdf`);
        if (!response.ok) {
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_report.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloading.value = false;
    }
}

async function onDownloadCsv() {
    if (downloadingCsv.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_CAMPAIGN_CSV_DOWNLOAD_CLICKED, { campaign_id: props.campaignId });
    downloadingCsv.value = true;
    csvError.value = null;
    try {
        const response = await fetchApi(`/v1/reflections/admin/campaigns/${props.campaignId}/export-csv`);
        if (!response.ok) {
            csvError.value = csvErrorMessage(await exportErrorCode(response));
            return;
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const disposition = response.headers.get("Content-Disposition");
        const match = disposition?.match(/filename="?([^"]+)"?/);
        a.download = match?.[1] ?? "campaign_scores.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } finally {
        downloadingCsv.value = false;
    }
}

const { regenerating, regenStatus, regenerate } = useReportRegeneration<CampaignResultsResponse>({
    fetchResults,
    generatedAt: (latest) => latest?.report?.generated_at ?? null,
    queueRegeneration: async () => {
        const { error: postError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/regenerate`, { method: "POST" }).json();
        return !postError.value;
    },
    onRegenerated: (latest) => {
        campaign.value = latest.campaign;
        report.value = latest.report ?? null;
    },
});

function onRegenerate() {
    void regenerate(report.value?.generated_at ?? null);
}

onMounted(async () => {
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${props.campaignId}/results`).json<CampaignResultsResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = fetchError.value.message || t("reflections.campaignReport.loadFailed");
        return;
    }
    campaign.value = data.value?.campaign ?? null;
    report.value = data.value?.report ?? null;
});
</script>
